// client/src/components/ReportInput/ReportInput.jsx 수정 버전

import React, { useState, useRef } from 'react';
import * as S from './ReportInput.style';
import { inputDataAPI } from '../../utils/axios'; // 🆕 Input Data API 추가

const ReportInput = ({ onSave, onCancel, isInputData = false }) => { // 🆕 isInputData prop 추가
  const [reportName, setReportName] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);

  // 🆕 Input Data 모드에서는 Excel 파일만 허용
  const ALLOWED_EXTENSIONS = isInputData ? ['xlsx', 'xls'] : ['xlsx', 'xls'];
  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

  const validateFile = (file) => {
    if (!file) {
      return { isValid: false, message: '파일을 선택해주세요.' };
    }

    // 파일 크기 검증
    if (file.size > MAX_FILE_SIZE) {
      return { 
        isValid: false, 
        message: `파일 크기가 너무 큽니다. 최대 50MB까지 업로드 가능합니다. (선택된 파일: ${(file.size / 1024 / 1024).toFixed(2)}MB)` 
      };
    }

    // 파일 확장자 검증
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
      return { 
        isValid: false, 
        message: `지원하지 않는 파일 형식입니다. (${ALLOWED_EXTENSIONS.join(', ')} 파일만 업로드 가능)` 
      };
    }

    return { isValid: true };
  };

  const handleReportNameChange = (event) => {
    setReportName(event.target.value);
  };

  const handleReportDescriptionChange = (event) => {
    setReportDescription(event.target.value);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    
    if (file) {
      const validation = validateFile(file);
      if (!validation.isValid) {
        alert(validation.message);
        // 파일 입력 초기화
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      
      // 이름이 비어있으면 파일명에서 확장자를 제거한 이름을 자동 입력
      if (!reportName.trim()) {
        const nameWithoutExtension = file.name.replace(/\.[^/.]+$/, '');
        setReportName(nameWithoutExtension);
      }
    }
  };

  const handleFileUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleSave = async () => {
    // 입력 검증
    if (!reportName.trim()) {
      alert(`${isInputData ? 'Input Data' : '보고서'}명을 입력해주세요.`);
      return;
    }
    
    if (!selectedFile) {
      alert(`${isInputData ? 'Input Data' : '입력 데이터'} 파일을 선택해주세요.`);
      return;
    }

    // 파일 재검증
    const validation = validateFile(selectedFile);
    if (!validation.isValid) {
      alert(validation.message);
      return;
    }

    setIsProcessing(true);

    try {
      console.log('[REPORT_INPUT]', `Creating ${isInputData ? 'input data' : 'report'}:`, {
        name: reportName.trim(),
        description: reportDescription.trim(),
        file: selectedFile.name
      });

      let result;
      
      if (isInputData) {
        // 🆕 Input Data 업로드
        result = await inputDataAPI.upload(
          selectedFile,
          reportName.trim(),
          reportDescription.trim()
        );
      } else {
        // 기존 보고서 로직 (임시)
        await new Promise(resolve => setTimeout(resolve, 1000));
        result = {
          success: true,
          data: {
            id: `rpt_${Date.now()}`,
            name: reportName.trim(),
            description: reportDescription.trim(),
            inputFileName: selectedFile.name,
            sheetsCount: Math.floor(Math.random() * 5) + 1,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }
        };
      }

      if (result.success) {
        onSave?.(result.data);
        
        // 성공 메시지
        if (isInputData) {
          alert(`Input Data가 성공적으로 추가되었습니다. (${result.data.tables_count}개 테이블 감지)`);
        } else {
          alert('보고서가 성공적으로 추가되었습니다.');
        }
        
        // 폼 초기화
        resetForm();
      } else {
        throw new Error(result.message);
      }
      
    } catch (error) {
      console.error('[REPORT_INPUT]', 'Save error:', error);
      alert(`${isInputData ? 'Input Data' : '보고서'} 추가 중 오류가 발생했습니다: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    if (isProcessing) {
      const confirmed = window.confirm(`${isInputData ? 'Input Data' : '보고서'} 추가가 진행 중입니다. 취소하시겠습니까?`);
      if (!confirmed) return;
    }

    resetForm();
    onCancel?.();
  };

  const resetForm = () => {
    setReportName('');
    setReportDescription('');
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <S.InputContainer>
      {/* 업로드 진행 중 오버레이 */}
      {isProcessing && (
        <S.ProcessingOverlay>
          <S.ProcessingProgress>
            <S.ProgressSpinner />
            <S.ProgressText>
              {isInputData ? '보고서 템플릿 추가 중...' : '보고서 추가 중...'}
            </S.ProgressText>
          </S.ProcessingProgress>
        </S.ProcessingOverlay>
      )}

      <S.FieldGroup>
        <S.FieldLabel>
          {isInputData ? '보고서 템플릿 이름' : '보고서 이름'} *
        </S.FieldLabel>
        <S.TextInput
          type="text"
          value={reportName}
          onChange={handleReportNameChange}
          placeholder={isInputData ? '보고서 템플릿 이름을 입력하세요' : '보고서 이름을 입력하세요'}
          disabled={isProcessing}
          maxLength={100}
        />
        <S.FieldHint>최대 100자까지 입력 가능합니다.</S.FieldHint>
      </S.FieldGroup>

      <S.FieldGroup>
        <S.FieldLabel>보고서 템플릿 설명</S.FieldLabel>
        <S.TextArea
          value={reportDescription}
          onChange={handleReportDescriptionChange}
          placeholder={isInputData ? '보고서 템플릿에 대한 설명을 입력하세요 (선택사항)' : '보고서에 대한 설명을 입력하세요 (선택사항)'}
          disabled={isProcessing}
          maxLength={500}
        />
        <S.FieldHint>최대 500자까지 입력 가능합니다.</S.FieldHint>
      </S.FieldGroup>

      <S.FieldGroup>
        <S.FieldLabel>
          {isInputData ? '보고서 템플릿 파일' : '보고서 템플릿 파일'} *
        </S.FieldLabel>
        <S.FileUploadArea onClick={handleFileUploadClick} disabled={isProcessing}>
          <S.UploadIcon>{isInputData ? '📋' : '📊'}</S.UploadIcon>
          {selectedFile ? (
            <S.FileInfo>
              <S.UploadText>{selectedFile.name}</S.UploadText>
              <S.FileDetails>
                크기: {formatFileSize(selectedFile.size)} | 
                형식: {selectedFile.type || 'Excel'}
              </S.FileDetails>
            </S.FileInfo>
          ) : (
            <S.UploadText>
              {isInputData 
                ? '보고서 템플릿 파일을 선택하거나 여기에 드래그하세요'
                : '입력 데이터 파일을 선택하거나 여기에 드래그하세요'
              }
            </S.UploadText>
          )}
          <S.HiddenFileInput
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
            disabled={isProcessing}
          />
        </S.FileUploadArea>
        <S.FieldHint>
          지원 형식: Excel (.xlsx, .xls)<br/>
          최대 파일 크기: 50MB<br/>
          {isInputData 
            ? '보고서 테이블 정의가 포함된 Excel 파일입니다. 업로드 시 자동으로 JSON 형태로 변환됩니다.'
            : 'autoreporter_core에서 처리할 보고서 조건 정의 파일입니다.'
          }
        </S.FieldHint>
      </S.FieldGroup>

      <S.ButtonGroup>
        <S.CancelButton onClick={handleCancel} disabled={isProcessing}>
          취소
        </S.CancelButton>
        <S.SaveButton 
          onClick={handleSave} 
          disabled={isProcessing || !reportName.trim() || !selectedFile}
        >
          {isProcessing ? '추가 중...' : '추가'}
        </S.SaveButton>
      </S.ButtonGroup>
    </S.InputContainer>
  );
};

export default ReportInput;